import { combine, withContext, withAsyncEffect } from 'react-hooks-combine'
import { StoreContext } from 'store/context'
import { ServicesContext } from 'services/context'

import TeamScheduleComponent from './component'

export const TeamSchedule = combine({
	hooks: [
		withContext('teamsStore', StoreContext, ({ teams }) => teams),
		withContext('eventsStore', StoreContext, ({ events }) => events),
		withContext('tournamentsStore', StoreContext, ({ tournaments }) => tournaments),
		withContext('teamService', ServicesContext, ({ teamService }) => teamService),

		// Load team and games data together to ensure consistency
		withAsyncEffect({
			deps: ['match'],
			dataName: 'teamData',
			loadingName: 'loading',
			errorName: 'error',
			async asyncAction({ teamsStore, eventsStore, tournamentsStore }, { match }) {
				const teamId = Number(match?.params?.teamId)
				const eventId = Number(match?.params?.eventId)

				if (!teamId || !eventId) {
					return { team: null, games: [] }
				}

				try {
					// Step 1: Try to load as tournament first, then fall back to event
					let tournamentId = null

					try {
						// Try loading as tournament
						const tournament = await tournamentsStore.loadOne(eventId)
						tournamentId = tournament.id
					} catch (tournamentError) {
						console.warn('Failed to load team schedule tournament; falling back to event load:', tournamentError)
						// Fall back to loading as event
						try {
							const event = await eventsStore.loadOne(eventId)
							tournamentId = event?.tournament?.id
						} catch (eventError) {
							console.warn('Failed to load team schedule event:', eventError)
							return { team: null, games: [], error: 'Tournament or event not found' }
						}
					}

					if (!tournamentId) {
						return { team: null, games: [] }
					}

					// Step 2: Load only future, unresolved games for this tournament
					const currentDate = Date.now()
					const { events: tournamentGames } = await eventsStore.load({
						tournamentId: tournamentId,
						size: 200,
						page: 0,
						sort: 'startTime,asc',
						startTimeEqOrGreater: currentDate, // Only future games
						resolvedStatus: 'NOT_RESOLVED' // Only unresolved games
					})

					// Step 3: Find games where this team participates
					const teamGames = tournamentGames?.filter(game => {
						const teamOneId = game.teamOne?.id
						const teamTwoId = game.teamTwo?.id
						return teamOneId === teamId || teamTwoId === teamId
					}) || []

					// Step 4: If no games found, this team doesn't participate in the tournament
					if (teamGames.length === 0) {
						return {
							team: { id: teamId, teamName: `Team ${teamId}` },
							games: [],
							error: 'This team does not participate in this tournament'
						}
					}

					// Step 5: Try to get team data from the games or team store
					let team = null

					// First try to get team info from the games themselves
					const gameWithTeam = teamGames.find(game =>
						game.teamOne?.id === teamId || game.teamTwo?.id === teamId
					)

					if (gameWithTeam) {
						const teamFromGame = gameWithTeam.teamOne?.id === teamId
							? gameWithTeam.teamOne
							: gameWithTeam.teamTwo

						if (teamFromGame?.teamName) {
							team = teamFromGame
						}
					}

					// If we still don't have team data, try loading from team store
					if (!team) {
						try {
							team = await teamsStore.loadOne(teamId)
						} catch (teamError) {
							console.warn('Failed to load team by ID; using fallback team data:', teamError)
							// Create basic team object with ID
							team = { id: teamId, teamName: `Team ${teamId}` }
						}
					}

					return {
						team,
						games: teamGames
					}

				} catch (error) {
					console.error('Failed to load team schedule:', error)
					return {
						team: { id: teamId, teamName: `Team ${teamId}` },
						games: [],
						error: 'Failed to load team schedule'
					}
				}
			},
		}),
	],
})(({ match, ...props }) => {
	// Extract team and games from teamData
	const { teamData, loading, error } = props
	const team = teamData?.team
	const teamGames = teamData?.games || []
	const dataError = teamData?.error || error

	return (
		<TeamScheduleComponent
			{...props}
			teamId={Number(match?.params?.teamId)}
			team={team}
			teamGames={teamGames}
			loading={loading}
			error={dataError}
			gamesLoading={false} // We load everything together now
			gamesError={null}
		/>
	)
}) 
